import React from "react";
import { Button } from "@material-ui/core";
import { FileCopyOutlined, Check } from "@material-ui/icons";
import { useResettingBoolean } from "@ui/scheduling-links/hooks/useResettingBoolean";
import { animated, useSpring } from "@react-spring/web";

type Props = {
  animate?: boolean;
  copyAriaLabel: string;
  disabled?: boolean;
  onCopy: () => unknown;
  startIcon?: JSX.Element;
  text?: string;
  urlFormatting?: boolean;
};

export function CopyText({
  animate = true,
  children,
  copyAriaLabel,
  disabled,
  onCopy,
  startIcon,
  text,
  urlFormatting = false,
}: React.PropsWithChildren<Props>) {
  const [copied, setCopied] = useResettingBoolean(false);
  const toCopy = text || children;

  const { textScale } = useSpring({
    textScale: copied ? 0 : 1,
    immediate: !copied || !animate, // Prevents a second animation when copied is returned to false.
    config: { mass: 2, tension: 180, friction: 26, precision: 0.01 },
  });

  const scaleInterpolateRange = [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1];
  const scaleInterpolateOutput = [1.007, 1.016, 1.032, 1.043, 1.047, 1.04, 1.022, 1.012, 1.006];

  const [hovering, setHovering] = React.useState(false);
  const [{ scale }] = useSpring(
    {
      scale: hovering ? 1.025 : 1.0,
      immediate: !animate,
      config: { mass: 1.1, tension: 155, friction: 20, precision: 0.01 },
    },
    [hovering],
  );

  return (
    <animated.div
      style={{
        transform: textScale
          .to({
            range: scaleInterpolateRange,
            output: scaleInterpolateOutput.map((s) => s - (s - 1) * 0.35),
          })
          .to((x) => `scale(${x})`),
        scale,
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`cw-flex cw-items-stretch ${
        copied ? "cw-bg-green-lightest" : "cw-bg-grey-lightest"
      } cw-justify-between cw-rounded-lg cw-min-w-min cw-transition-colors cw-duration-300 cw-delay-75`}
    >
      <div className="cw-flex cw-flex-col cw-items-center">
        <div className="cw-mx-2 cw-pl-1 cw-h-full cw-text-slate-standard cw-inline-flex cw-items-center">
          {startIcon}
          <span
            className={`cw-px-2 cw-py-[14px] cw-cursor-default cw-leading-[1.15rem] ${
              urlFormatting ? "break-all" : ""
            }`}
          >
            {toCopy}
          </span>
        </div>
      </div>

      <Button
        onClick={() => {
          onCopy();
          setCopied(true);
        }}
        disabled={disabled}
        variant="contained"
        className="cw-rounded-lg cw-inline-flex cw-px-[11px] cw-py-2 cw-m-2 cw-min-w-[46px] cw-max-h-[40px]"
        color="primary"
        aria-label={copyAriaLabel}
      >
        {copied ? <Check /> : <FileCopyOutlined />}
      </Button>
    </animated.div>
  );
}
